/* eslint-disable no-useless-escape */
import * as React from 'react';
import { stateToMarkdown } from 'draft-js-export-markdown';
import { stateFromMarkdown } from 'draft-js-import-markdown';
import { EditorState } from 'draft-js';

import { trim } from 'lodash';
import { UNWANTED_LAST_CHAR_REGEX, URL_REGEX } from 'utils/url';
import type ContentBlock from 'draft-js/lib/ContentBlock';

function removeEmptyTags(content: string): string {
  const matchEmptyTags = /(\+\+|~~|_){1}([\+\+|~~|_ \t]*)(\1){1}/g;
  return content.replace(matchEmptyTags, '$2');
}

export function patchBuggyMarkdownSpaces(content: string): string {
  // moves prefix and postfix spaces outside of tags to fix rendering bugs: `_foo _` -> `_foo_ `
  const matchBuggySpaces = /(\+\+|~~|_){1}([ \t]*)([^\1]*?)([ \t]*)(\1){1}/g;
  const substitution = (substring, m1, m2, m3, m4, m5) => (
    `${m2}${m1}${patchBuggyMarkdownSpaces(m3)}${m5}${m4}`
  );

  const matchAnyTags = /(\+\+|~~|_){1}.*?(\1){1}/g;

  let previousString;
  let nextString = content;
  do {
    previousString = nextString;
    nextString = previousString
      .replace(matchBuggySpaces, substitution)
      .replace(matchAnyTags, removeEmptyTags);
  }
  while (previousString !== nextString);

  return nextString;
}

export function patchBuggyListNewlines(content: string): string {
  const matchBuggyNewlines = /(^ *([*+-]|\d+\.) .+\n)\n( *(\d+\.) .+)/mg;
  const substitution = '$1$3';

  let previousString;
  let nextString = content;
  do {
    previousString = nextString;
    nextString = previousString
      .replace(matchBuggyNewlines, substitution);
  }
  while (previousString !== nextString);

  return nextString;
}

export function toMarkdown(editorState: typeof EditorState): string {
  // collab-mobile expects certain markdown rules, don't change this code without testing mobile
  const literalMarkdown = stateToMarkdown(editorState.getCurrentContent());
  const patchedMarkdown = patchBuggyMarkdownSpaces(literalMarkdown);
  const patchedNewlines = patchBuggyListNewlines(patchedMarkdown);

  // Remove prefixed/suffixed newlines and zero-width spaces
  const re = /(^[\s\u200B]*)|([\s\u200B]*$)/gi;
  const trimmedValue = patchedNewlines.trim().replace(re, '');

  return trimmedValue;
}

// $FlowIgnore flow is not able to pickup ContentBlock from third party
export function urlStrategy(contentBlock: ContentBlock, callback: (b: number, a: number) => void): void {
  const text = contentBlock.getText();
  let matchArr;
  matchArr = URL_REGEX.exec(text);
  while (matchArr !== null) {
    const start = matchArr.index;
    const length = matchArr[0].length;
    // The URL_REGEX can include some unwanted last characters, here we move them out
    const offset = UNWANTED_LAST_CHAR_REGEX.test(matchArr[0]) ? length - 1 : length;
    callback(start, start + offset);
    matchArr = URL_REGEX.exec(text);
  }
}

export function isEmptyEditorText(value: string = ''): boolean {
  const re = /[\s\u200B]/gi;
  const strippedValue = value.replace(re, '');

  return trim(strippedValue).length === 0;
}

export function getEditorState(text: React.ReactNode = ''): typeof EditorState {
  if (!text) {
    return EditorState.createEmpty();
  }

  return EditorState.createWithContent(stateFromMarkdown(text));
}

export function getPlainText(text: string): string {
  const editorState = getEditorState(text);

  return editorState.getCurrentContent().getPlainText('\n');
}
